// src/utilities/logSanitizer.ts

/**
 * Sanitizes a string to prevent log injection (CWE-117) and other
 * output-related vulnerabilities.
 * @param input The string to sanitize.
 * @returns The sanitized string.
 */
export const sanitizeForLog = (input: string | undefined): string | undefined => {
  if (typeof input !== 'string') {
    return input;
  }

  // 1. Neutralize control characters that can be used for log injection.
  //    This includes newlines (\r, \n) and tabs (\t), which can forge new log entries.
  let sanitized = input.replace(/[\r\n\t]+/g, ' ');

  // 2. Neutralize Unicode line and paragraph separators.
  //    These characters are often used to bypass basic newline checks.
  sanitized = sanitized.replace(/[\u2028\u2029]+/g, ' ');

  // 3. Encode characters that could be interpreted as HTML.
  //    This is a good practice for logs that might be viewed in an HTML context (e.g., a web-based log viewer).
  //    It prevents vulnerabilities like Cross-Site Scripting (XSS).
  sanitized = sanitized.replace(/[<>&'"]/g, c => {
    switch (c) {
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '&': return '&amp;';
      case '\'': return '&#39;';
      case '"': return '&quot;';
      default: return c;
    }
  });

  // 4. Handle other non-printable or special characters.
  //    This step is more of a defensive measure to ensure only safe characters are logged.
  //    We'll combine the regex from Helpers.ts, but simplify the replacement to just remove them,
  //    which is generally safer and less complex than character code encoding for logs.
  //    The list of allowed characters is intentionally broad.
  sanitized = sanitized.replace(/[^a-zA-Z0-9 .,;:!@#$%^&*()+=_-]/g, '');

  return sanitized;
};